var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { Emitter } from "./event";
export var TreeVisibility;
(function (TreeVisibility) {
    /**  Hide the node */
    TreeVisibility[TreeVisibility["Hidden"] = 0] = "Hidden";
    /** Show the node if any children are visible */
    TreeVisibility[TreeVisibility["Recurse"] = 1] = "Recurse";
    /** Show the node */
    TreeVisibility[TreeVisibility["Visible"] = 2] = "Visible";
    /** Show the node and all of it's children */
    TreeVisibility[TreeVisibility["Tree"] = 3] = "Tree";
})(TreeVisibility || (TreeVisibility = {}));
function isCollapsibleStateUpdate(update) {
    return typeof update.collapsible === "boolean";
}
var TreeModel = /** @class */ (function () {
    function TreeModel(list, rootElement, options) {
        this.list = list;
        this.options = options;
        this._filter = undefined;
        this._onDidFilterChange = new Emitter();
        this.onDidFilterChange = this._onDidFilterChange.event;
        this._root = {
            parent: undefined,
            element: rootElement,
            children: [],
            depth: 0,
            visibleChildrenCount: 0,
            visibleChildIndex: -1,
            collapsible: false,
            collapsed: false,
            renderNodeCount: 0,
            visible: true,
        };
    }
    Object.defineProperty(TreeModel.prototype, "filter", {
        get: function () {
            return this._filter;
        },
        set: function (value) {
            this._filter = value;
            this._onDidFilterChange.fire();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TreeModel.prototype, "root", {
        get: function () {
            return this._root;
        },
        enumerable: false,
        configurable: true
    });
    TreeModel.prototype.dispose = function () {
        this._onDidFilterChange.dispose();
    };
    TreeModel.prototype.splice = function (location, deleteCount, toInsert, onDidCreateNode, onDidDeleteNode) {
        var _a, e_1, _b;
        var _this = this;
        if (location.length === 0) {
            throw new Error("Invalid tree location");
        }
        var parentNode = this.getParentNodeWithListIndex(location).parentNode;
        var listNodes = [];
        var nodesToInsert = toInsert.map(function (el) {
            return _this.createTreeNode(el, parentNode, parentNode.visible ? TreeVisibility.Visible : TreeVisibility.Hidden, listNodes, onDidCreateNode);
        });
        var lastIndex = location[location.length - 1];
        var visibleChildStartIndex = 0;
        for (var i = lastIndex; i >= 0 && i < parentNode.children.length; i--) {
            var child = parentNode.children[i];
            if (child.visible) {
                visibleChildStartIndex = child.visibleChildIndex;
                break;
            }
        }
        var insertedVisibleChildrenCount = 0;
        var renderNodeCount = 0;
        nodesToInsert.forEach(function (child) {
            renderNodeCount += child.renderNodeCount;
            if (child.visible) {
                child.visibleChildIndex =
                    visibleChildStartIndex + insertedVisibleChildrenCount++;
            }
        });
        var deletedNodes = (_a = parentNode.children).splice.apply(_a, __spreadArray([lastIndex,
            deleteCount], __read(nodesToInsert)));
        var deletedVisibleChildrenCount = 0;
        try {
            for (var deletedNodes_1 = __values(deletedNodes), deletedNodes_1_1 = deletedNodes_1.next(); !deletedNodes_1_1.done; deletedNodes_1_1 = deletedNodes_1.next()) {
                var child = deletedNodes_1_1.value;
                if (child.visible) {
                    deletedVisibleChildrenCount++;
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (deletedNodes_1_1 && !deletedNodes_1_1.done && (_b = deletedNodes_1.return)) _b.call(deletedNodes_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        // and adjust for all visible children after the splice point
        if (deletedVisibleChildrenCount !== 0) {
            for (var i = lastIndex + nodesToInsert.length; i < parentNode.children.length; i++) {
                var child = parentNode.children[i];
                if (child.visible) {
                    child.visibleChildIndex -= deletedVisibleChildrenCount;
                }
            }
        }
        parentNode.visibleChildrenCount += insertedVisibleChildrenCount;
        if (deletedNodes.length > 0 && onDidDeleteNode) {
            var visit_1 = function (node) {
                onDidDeleteNode(node);
                node.children.forEach(visit_1);
            };
            deletedNodes.forEach(visit_1);
        }
        this._updateAncestorsRenderCount(parentNode, renderNodeCount);
        return deletedNodes;
    };
    TreeModel.prototype.asList = function () {
        var result = [];
        this.toList(this.root, this.root.visibleChildrenCount
            ? TreeVisibility.Visible
            : TreeVisibility.Hidden, result);
        return result;
    };
    TreeModel.prototype.setCollapsed = function (node, collapsed, recursive) {
        var location = this.getNodeLocation(node);
        var _a = this.getTreeNodeWithListIndex(location), _node = _a.node, listIndex = _a.listIndex;
        var update = {
            collapsed: collapsed,
            recursive: recursive || false,
        };
        var result = this._setListNodeCollapseState(_node, listIndex, update);
        // autoExpandSingleChildren
        return result;
    };
    TreeModel.prototype.getNodeLocation = function (node) {
        var location = [];
        while (node.parent) {
            location.push(node.parent.children.indexOf(node));
            node = node.parent;
        }
        return location.reverse();
    };
    TreeModel.prototype.getTreeNodeWithListIndex = function (location) {
        if (location.length === 0) {
            return { node: this.root, listIndex: -1, revealed: true, visible: false };
        }
        var _a = this.getParentNodeWithListIndex(location), parentNode = _a.parentNode, listIndex = _a.listIndex, visible = _a.visible;
        var index = location[location.length - 1];
        if (index < 0 || index > parentNode.children.length) {
            throw new Error("Invalid tree location");
        }
        var node = parentNode.children[index];
        return { node: node, listIndex: listIndex, visible: visible && node.visible };
    };
    TreeModel.prototype._setListNodeCollapseState = function (node, listIndex, collapsedState) {
        var _a;
        var result = this._setNodeCollapseState(node, collapsedState);
        if (!node.visible || !result) {
            return result;
        }
        var previousRenderNodeCount = node.renderNodeCount;
        var toInsert = this.updateNodeAfterCollapseChange(node);
        var deleteCount = previousRenderNodeCount - (listIndex === -1 ? 0 : 1);
        (_a = this.list).splice.apply(_a, __spreadArray([listIndex + 1, deleteCount], __read(toInsert.slice(1))));
        return result;
    };
    TreeModel.prototype._setNodeCollapseState = function (node, update) {
        var e_2, _a;
        var result;
        if (node === this.root) {
            result = false;
        }
        else {
            if (isCollapsibleStateUpdate(update)) {
                result = node.collapsible !== update.collapsible;
            }
            else if (!node.collapsible) {
                result = false;
            }
            else {
                result = node.collapsed !== update.collapsed;
                node.collapsed = update.collapsed;
            }
            if (result) {
                // this._onDidChangeCollapseState.fire({ node, deep });
            }
        }
        if (!isCollapsibleStateUpdate(update) && update.recursive) {
            try {
                for (var _b = __values(node.children), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var child = _c.value;
                    result = this._setNodeCollapseState(child, update) || result;
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
        return result;
    };
    TreeModel.prototype.updateNodeAfterCollapseChange = function (node) {
        var previousRenderNodeCount = node.renderNodeCount;
        var result = [];
        this._updateNodeAfterCollapseChange(node, result);
        this._updateAncestorsRenderNodeCount(node.parent, result.length - previousRenderNodeCount);
        return result;
    };
    TreeModel.prototype._updateAncestorsRenderNodeCount = function (node, diff) {
        if (diff === 0) {
            return;
        }
        while (node) {
            node.renderNodeCount += diff;
            // this._onDidChangeRenderNodeCount.fire(node);
            node = node.parent;
        }
    };
    TreeModel.prototype._updateNodeAfterCollapseChange = function (node, result) {
        var e_3, _a;
        if (node.visible === false) {
            return 0;
        }
        result.push(node);
        node.renderNodeCount = 1;
        if (!node.collapsed) {
            try {
                for (var _b = __values(node.children), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var child = _c.value;
                    node.renderNodeCount += this._updateNodeAfterCollapseChange(child, result);
                }
            }
            catch (e_3_1) { e_3 = { error: e_3_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_3) throw e_3.error; }
            }
        }
        // this._onDidChangeRenderNodeCount.fire(node);
        return node.renderNodeCount;
    };
    TreeModel.prototype._filterNode = function (node, parentVisibility) {
        var visible;
        if (parentVisibility === TreeVisibility.Tree) {
            return TreeVisibility.Tree;
        }
        if (this.filter) {
            visible = this.filter(node.element);
            return visible;
        }
        if (node.visible) {
            return TreeVisibility.Visible;
        }
        return TreeVisibility.Hidden;
    };
    TreeModel.prototype.toList = function (node, parentVisibility, result, revealed) {
        var e_4, _a;
        if (revealed === void 0) { revealed = true; }
        var visibility = parentVisibility;
        if (node !== this.root) {
            visibility = this._filterNode(node, parentVisibility);
            if (visibility === TreeVisibility.Hidden) {
                node.visible = false;
                node.renderNodeCount = 0;
                return false;
            }
            if (revealed) {
                result.push(node);
            }
        }
        var resultStartLength = result.length;
        node.renderNodeCount = node === this.root ? 0 : 1;
        var hasVisibleDescendants = false;
        if (!node.collapsed || visibility !== TreeVisibility.Hidden) {
            var visibleChildIndex = 0;
            try {
                for (var _b = __values(node.children), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var child = _c.value;
                    hasVisibleDescendants =
                        this.toList(child, visibility, result, revealed && !node.collapsed) ||
                            hasVisibleDescendants;
                    if (child.visible) {
                        child.visibleChildIndex = visibleChildIndex++;
                    }
                }
            }
            catch (e_4_1) { e_4 = { error: e_4_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_4) throw e_4.error; }
            }
            node.visibleChildrenCount = visibleChildIndex;
        }
        else {
            node.visibleChildrenCount = 0;
        }
        if (node !== this.root) {
            // if (hasVisibleDescendants) {
            //   node.collapsed = false;
            // }
            node.visible =
                visibility === TreeVisibility.Recurse
                    ? hasVisibleDescendants
                    : visibility === TreeVisibility.Visible ||
                        visibility === TreeVisibility.Tree;
        }
        if (!node.visible) {
            node.renderNodeCount = 0;
            if (revealed) {
                result.pop();
            }
        }
        else if (!node.collapsed) {
            node.renderNodeCount += result.length - resultStartLength;
        }
        // this._onDidChangeRenderNodeCount.fire(node);
        return node.visible;
    };
    TreeModel.prototype._updateAncestorsRenderCount = function (node, diff) {
        if (diff === 0) {
            return;
        }
        var _node = node;
        while (_node) {
            _node.renderNodeCount += diff;
            // this._onDidChangeRenderNodeCount.fire(node);
            _node = _node.parent;
        }
    };
    TreeModel.prototype.createTreeNode = function (treeElement, parent, parentVisibility, treeListElements, onDidCreateNode) {
        var _this = this;
        var node = {
            parent: parent,
            element: treeElement.element,
            children: [],
            depth: parent.depth + 1,
            visibleChildrenCount: 0,
            visibleChildIndex: -1,
            collapsible: typeof treeElement.collapsible === "boolean"
                ? treeElement.collapsible
                : typeof treeElement.collapsed !== "undefined",
            collapsed: typeof treeElement.collapsed === "undefined"
                ? this.options.collapseByDefault
                : treeElement.collapsed,
            renderNodeCount: 1,
            visible: true,
            height: treeElement.height,
        };
        var childElements = treeElement.children || [];
        var childNodes = childElements.map(function (el) {
            return _this.createTreeNode(el, node, parentVisibility, treeListElements, onDidCreateNode);
        });
        var visibleChildrenCount = 0;
        var renderNodeCount = 1;
        childNodes.forEach(function (child) {
            node.children.push(child);
            renderNodeCount += child.renderNodeCount;
            if (child.visible) {
                child.visibleChildIndex = visibleChildrenCount++;
            }
        });
        node.collapsible = node.collapsible || node.children.length > 0;
        node.visibleChildrenCount = visibleChildrenCount;
        // node.visible =
        //   visibility === TreeVisibility.Recurse
        //     ? visibleChildrenCount > 0
        //     : visibility === TreeVisibility.Visible;
        if (!node.visible) {
            node.renderNodeCount = 0;
            // if (revealed) {
            //   treeListElements.pop();
            // }
        }
        else if (!node.collapsed) {
            node.renderNodeCount = renderNodeCount;
        }
        if (onDidCreateNode) {
            onDidCreateNode(node);
        }
        return node;
    };
    TreeModel.prototype.getParentNodeWithListIndex = function (location, node, listIndex, visible) {
        if (node === void 0) { node = this._root; }
        if (listIndex === void 0) { listIndex = 0; }
        if (visible === void 0) { visible = true; }
        var _a = __read(location), index = _a[0], rest = _a.slice(1);
        if (index < 0 || index > node.children.length) {
            throw new Error("Invalid tree location");
        }
        for (var i = 0; i < index; i++) {
            listIndex += node.children[i].renderNodeCount;
        }
        visible = visible && node.visible;
        if (rest.length === 0) {
            return { parentNode: node, listIndex: listIndex, visible: visible };
        }
        return this.getParentNodeWithListIndex(rest, node.children[index], listIndex + 1, visible);
    };
    return TreeModel;
}());
export { TreeModel };
